// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import type {
  AccountCurrency,
  AdvisoryAccountsDebt,
} from '@shared/data-access/types/brokerage-types';
import { isLoanExpired } from '../utils/debt-amortizations';

export interface UseAccountsDebtProps {
  clientId?: number | null;
  ibans: string[];
  institutionId: string;
  skip?: boolean;
  currency: AccountCurrency;
  start_date: string;
  end_date: string;
}

export const useAccountsDebt = ({
  clientId,
  ibans,
  institutionId,
  skip,
  currency,
  start_date,
  end_date,
}: UseAccountsDebtProps) => {
  const accountsDebtQuery = advisoryApi.useGetAdvisoryAccountsDebtQuery(
    {
      client_id: clientId,
      currency,
      start_date,
      end_date,
      institution_id: institutionId,
    },
    { skip },
  );

  const accountsDebt = useMemo((): AdvisoryAccountsDebt | undefined => {
    const data = accountsDebtQuery.data;

    if (!data) {
      return undefined;
    }

    const accounts = data.accounts
      .filter(account => account.iban && ibans.includes(account.iban))
      .map(account => ({
        ...account,
        loans: (account.loans ?? []).filter(loan => !isLoanExpired(loan)),
      }));

    return {
      ...data,
      accounts,
    };
  }, [accountsDebtQuery.data, ibans]);

  const hasDebtData = useMemo(
    () =>
      (accountsDebt?.accounts ?? []).some(
        account =>
          (account.loans?.length ?? 0) > 0 ||
          (account.credit_lines?.length ?? 0) > 0,
      ),
    [accountsDebt],
  );

  const hasDebtChartData = useMemo(
    () => (accountsDebt?.dates.length ?? 0) > 0,
    [accountsDebt],
  );

  return {
    accountsDebt,
    hasDebtData,
    hasDebtChartData,
    accountsDebtQuery,
  };
};
